import React, { useEffect, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { ModalConfirmAction } from "components/modals/ModalConfirmAction"

import { AdminBonus2Service } from "services/bonus/AdminBonus2Service"
import { AdminBonus2, AdminBonus2Simple, AdminPlayerBonus2, BulkAssignResult } from "services/bonus/type"

import { formatDateTime, formatFiatCurrency } from "util/formatter"

import { getDisplayName } from "../../helpers/bonus2.helpers"
import { formatStake } from "../../helpers/spin-stake.helpers"
import { UserPicker } from "../shared/UserPicker"

import { BulkAssignResults } from "./BulkAssignResults"
import { TagAudiencePicker } from "./TagAudiencePicker"
import { useTagAudience } from "./useTagAudience"

interface AssignBonusProps {
  tenant: string
  // Iteration-3 Fix 03 — when embedded in a user's detail "Bonuses" tab, the user is fixed:
  // hide the picker/heading and notify the parent after a successful assign so it can refresh.
  fixedUserId?: string
  onAssigned?: () => void
}

export const AssignBonus: React.FC<AssignBonusProps> = ({ tenant, fixedUserId, onAssigned }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const isEmbedded = Boolean(fixedUserId)
  const [bonuses, setBonuses] = useState<AdminBonus2Simple[]>([])
  const [selectedBonus, setSelectedBonus] = useState<AdminBonus2 | null>(null)
  const [userId, setUserId] = useState(fixedUserId ?? "")
  const [selectedBonusId, setSelectedBonusId] = useState("")
  const [depositAmount, setDepositAmount] = useState("")
  const [loading, setLoading] = useState(false)
  const [assignedBonus, setAssignedBonus] = useState<AdminPlayerBonus2 | null>(null)

  const [mode, setMode] = useState<"user" | "tag">("user")
  const [tagSlugs, setTagSlugs] = useState<string[]>([])
  const [confirmBulk, setConfirmBulk] = useState(false)
  const [bulkResult, setBulkResult] = useState<BulkAssignResult | null>(null)
  const bulkMode = !isEmbedded && mode === "tag"
  const { audience, loading: audienceLoading } = useTagAudience(tagSlugs, tenant, bulkMode)

  useEffect(() => {
    loadBonuses()
  }, [tenant])

  // Fix 03 — keep the fixed user in sync when the embedding page swaps the target user.
  useEffect(() => {
    if (fixedUserId !== undefined) setUserId(fixedUserId)
  }, [fixedUserId])

  const loadBonuses = async () => {
    const data = await AdminBonus2Service.listBonuses(tenant, true)
    if (data) {
      setBonuses(data)
    }
  }

  const handleBonusSelect = async (bonusId: string) => {
    setSelectedBonusId(bonusId)
    setSelectedBonus(null)

    if (bonusId) {
      const fullBonus = await AdminBonus2Service.getBonus(bonusId, tenant)
      if (fullBonus) {
        setSelectedBonus(fullBonus)
      }
    }
  }

  const handleAssignBonus = async () => {
    if (!userId.trim()) {
      toast.error(t("bonus2.players.toastUserIdRequired") as string)
      return
    }

    if (!selectedBonusId) {
      toast.error(t("bonus2.players.toastBonusRequired") as string)
      return
    }

    setLoading(true)

    try {
      // Step 1: Subscribe user to bonus. Coin is intentionally NOT sent — bonus-service
      // denominates the bonus in the player's OWN wallet coin (resolved from
      // financial-service). The admin must not choose the currency.
      const playerBonus = await AdminBonus2Service.subscribeUserToBonus(userId.trim(), selectedBonusId, tenant)

      if (playerBonus) {
        toast.success(t("bonus2.players.toastAssigned") as string)
        setAssignedBonus(playerBonus)
        // Fix 03 — let the embedding manager refresh its list to show the new subscription.
        onAssigned?.()
      } else {
        toast.error(t("bonus2.players.toastAssignFailed") as string)
      }
    } catch (error) {
      toast.error(t("bonus2.players.toastAssignFailed") as string)
    } finally {
      setLoading(false)
    }
  }

  const handleActivateBonus = async () => {
    if (!assignedBonus) return

    setLoading(true)

    try {
      // Step 2: Activate the bonus
      const deposit = depositAmount ? parseFloat(depositAmount) : null
      const activated = await AdminBonus2Service.activatePlayerBonus(assignedBonus, deposit, tenant)

      if (activated) {
        toast.success(t("bonus2.players.toastActivated") as string)
        setAssignedBonus(activated)
      } else {
        toast.error(t("bonus2.players.toastActivateFailed") as string)
      }
    } catch (error) {
      toast.error(t("bonus2.players.toastActivateFailed") as string)
    } finally {
      setLoading(false)
    }
  }

  const handleBulkAssign = async () => {
    if (loading) return
    setLoading(true)
    try {
      const result = await AdminBonus2Service.subscribeUsersByTag(selectedBonusId, tagSlugs, tenant)
      if (result) {
        setBulkResult(result)
        if (result.assigned > 0) toast.success(t("bonus2.players.toastBulkAssigned", { count: result.assigned }) as string)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    setUserId("")
    setSelectedBonusId("")
    setSelectedBonus(null)
    setDepositAmount("")
    setAssignedBonus(null)
    setTagSlugs([])
    setBulkResult(null)
  }

  const handleUserSelect = (id: string, _name: string) => {
    setUserId(id)
  }

  return (
    <div className="space-y-6">
      <div className="bg-[#1A1A2E] border border-gray-700 rounded-lg p-6">
        {/* Fix 03 — hide the standalone heading when embedded in a user's detail tab. */}
        {!isEmbedded && <h2 className="text-2xl font-bold text-white mb-6">{t("bonus2.players.assignBonusToUser")}</h2>}

        {/* Step 1: Assign Bonus */}
        {!assignedBonus && !bulkResult && (
          <div className="space-y-4">
            {/* Fix 03 — hide the target picker when the user is fixed by the embedding page. */}
            {!isEmbedded && (
              <div>
                <div className="flex gap-2 mb-3">
                  {(["user", "tag"] as const).map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setMode(m)}
                      className={`px-4 py-2 rounded text-sm transition-colors ${
                        mode === m ? "bg-purple text-white" : "bg-[#0C0C1A] text-gray-400 border border-gray-600 hover:text-white"
                      }`}
                    >
                      {t(m === "user" ? "bonus2.players.modeIndividual" : "bonus2.players.modeByTag")}
                    </button>
                  ))}
                </div>

                {mode === "user" ? (
                  <>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t("bonus2.players.searchUser")}
                      <span className="text-gray-500 ml-2 text-xs">{t("bonus2.players.searchByUsernameOr")}</span>
                    </label>
                    <UserPicker selectedUserId={userId} onSelect={handleUserSelect} disabled={loading} />
                  </>
                ) : (
                  <TagAudiencePicker
                    value={tagSlugs}
                    onChange={setTagSlugs}
                    audience={audience}
                    audienceLoading={audienceLoading}
                    disabled={loading}
                  />
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">{t("bonus2.players.selectBonus")}</label>
              <select
                value={selectedBonusId}
                onChange={(e) => handleBonusSelect(e.target.value)}
                className="w-full bg-[#0C0C1A] text-white border border-gray-600 rounded px-4 py-2 focus:outline-none focus:border-purple-500"
                disabled={loading}
              >
                <option value="">-- Select Bonus --</option>
                {bonuses.map((bonus) => (
                  <option key={bonus.id} value={bonus.id}>
                    {getDisplayName(bonus.name)} ({bonus.type})
                  </option>
                ))}
              </select>
            </div>

            {/* Currency is the player's OWN wallet coin — not an admin choice. The old
                dropdown let an admin pick a currency that could differ from the wallet,
                activating the bonus in the wrong coin. bonus-service resolves it from
                financial-service at subscribe time. */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">{t("bonus2.players.currency")}</label>
              <div className="w-full bg-[#0C0C1A] border border-gray-700 rounded px-4 py-2 text-gray-400 text-sm">
                Uses the player&apos;s wallet currency automatically.
              </div>
            </div>

            {/* Show bonus details */}
            {selectedBonus && (
              <div className="bg-[#0C0C1A] border border-gray-600 rounded p-4 space-y-3">
                <h3 className="text-white font-semibold mb-2">{t("bonus2.players.bonusConfiguration")}</h3>

                {/* Basic Info */}
                <div>
                  <div className="text-xs text-purple-400 mb-1 font-semibold">{t("bonus2.players.basicInfo")}</div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-400">{t("bonus2.players.type")} </span>
                      <span className="text-white">{selectedBonus.type}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">{t("bonus2.players.mode")} </span>
                      <span className="text-white">{selectedBonus.mode}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">{t("bonus2.players.activation")} </span>
                      <span className="text-white">{selectedBonus.activation_event}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">{t("bonus2.players.priority")} </span>
                      <span className="text-white">{selectedBonus.priority}</span>
                    </div>
                  </div>
                </div>

                {/* Amount Configuration */}
                <div>
                  <div className="text-xs text-purple-400 mb-1 font-semibold">{t("bonus2.players.amount")}</div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-400">{t("bonus2.players.calculation")} </span>
                      <span className="text-white">{selectedBonus.calculation_mode}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">{t("bonus2.players.amount2")} </span>
                      <span className="text-white">
                        {selectedBonus.calculation_mode === "PERCENTAGE"
                          ? `${selectedBonus.amount}%`
                          : selectedBonus.calculation_mode === "AI_DYNAMIC"
                          ? `$${selectedBonus.amount_min} - $${selectedBonus.amount_max}`
                          : `$${selectedBonus.amount}`}
                      </span>
                    </div>
                    {selectedBonus.max_bonus_amount && (
                      <div>
                        <span className="text-gray-400">{t("bonus2.players.maxBonus")} </span>
                        <span className="text-white">${selectedBonus.max_bonus_amount}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Wagering Configuration */}
                <div>
                  <div className="text-xs text-purple-400 mb-1 font-semibold">{t("bonus2.players.wagering")}</div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-400">{t("bonus2.players.multiplier")} </span>
                      <span className="text-white">{selectedBonus.wagering_multiplier}x</span>
                    </div>
                    <div>
                      <span className="text-gray-400">{t("bonus2.players.base")} </span>
                      <span className="text-white text-xs">{selectedBonus.wagering_base}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">{t("bonus2.players.contribution")} </span>
                      <span className="text-white text-xs">{selectedBonus.wagering_contribution}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">{t("bonus2.players.accounts")} </span>
                      <span className="text-white text-xs">{selectedBonus.wagering_accounts}</span>
                    </div>
                    {selectedBonus.max_bet_bonus && (
                      <div>
                        <span className="text-gray-400">{t("bonus2.players.maxBetBonus")} </span>
                        <span className="text-white">${selectedBonus.max_bet_bonus}</span>
                      </div>
                    )}
                    {selectedBonus.max_bet_wagering && (
                      <div>
                        <span className="text-gray-400">{t("bonus2.players.maxBetWagering")} </span>
                        <span className="text-white">${selectedBonus.max_bet_wagering}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Free Spins Configuration */}
                {(selectedBonus.type === "FREE_SPINS" || selectedBonus.type === "DEPOSIT_AND_SPINS" || selectedBonus.free_spins_count) && (
                  <div>
                    <div className="text-xs text-purple-400 mb-1 font-semibold">{t("bonus2.players.freeSpins")}</div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {selectedBonus.free_spins_count && (
                        <div>
                          <span className="text-gray-400">{t("bonus2.players.spinsCount")} </span>
                          <span className="text-white">{selectedBonus.free_spins_count}</span>
                        </div>
                      )}
                      {selectedBonus.free_spins_bet_value && (
                        <div>
                          {/* SOFT-736 — the template's stake is USD MINOR units. Printing it raw
                              showed "2000" for a $20 stake; it is money, so it is rendered as
                              money, and against the currency it is actually stated in. */}
                          <span className="text-gray-400">{t("bonus2.players.betValue")} </span>
                          <span className="text-white">{formatStake(selectedBonus.free_spins_bet_value, "USD")}</span>
                        </div>
                      )}
                      {selectedBonus.free_spins_wagering !== undefined && (
                        <div>
                          <span className="text-gray-400">{t("bonus2.players.wagering2")} </span>
                          <span className="text-white">{selectedBonus.free_spins_wagering}x</span>
                        </div>
                      )}
                      {selectedBonus.free_spins_expiration_days && (
                        <div>
                          <span className="text-gray-400">{t("bonus2.players.expires")} </span>
                          <span className="text-white">{selectedBonus.free_spins_expiration_days} days</span>
                        </div>
                      )}

                      {selectedBonus.free_spins_game_ids && selectedBonus.free_spins_game_ids.length > 0 && (
                        <div className="col-span-2">
                          <span className="text-gray-400">Eligible Games ({selectedBonus.free_spins_game_ids.length}): </span>
                          <div className="mt-1 max-h-32 overflow-y-auto bg-[#0A0A14] rounded p-2">
                            <span className="text-white text-xs font-mono">{selectedBonus.free_spins_game_ids.join(", ")}</span>
                          </div>
                        </div>
                      )}
                      {(!selectedBonus.free_spins_game_ids || selectedBonus.free_spins_game_ids.length === 0) && (
                        <div className="col-span-2">
                          <span className="text-gray-400">{t("bonus2.players.eligibleGames")} </span>
                          <span className="text-emerald-400 text-xs">{t("bonus2.players.allSlotGames")}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Limits & Expiration */}
                <div>
                  <div className="text-xs text-purple-400 mb-1 font-semibold">{t("bonus2.players.limitsExpiration")}</div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-400">{t("bonus2.players.expiration")} </span>
                      <span className="text-white">{selectedBonus.expiration_days} days</span>
                    </div>
                    {selectedBonus.max_cashout_multiplier && (
                      <div>
                        <span className="text-gray-400">{t("bonus2.players.maxCashout")} </span>
                        <span className="text-white">{selectedBonus.max_cashout_multiplier}x</span>
                      </div>
                    )}
                    {selectedBonus.max_activations_per_player && (
                      <div>
                        <span className="text-gray-400">{t("bonus2.players.maxClaims")} </span>
                        <span className="text-white">{selectedBonus.max_activations_per_player}</span>
                      </div>
                    )}
                    {selectedBonus.cooldown_days && (
                      <div>
                        <span className="text-gray-400">{t("bonus2.players.cooldown")} </span>
                        <span className="text-white">{selectedBonus.cooldown_days} days</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Stacking Mode */}
                <div>
                  <div className="text-xs text-purple-400 mb-1 font-semibold">{t("bonus2.players.stackingMode")}</div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-400">{t("bonus2.players.stackingMode2")} </span>
                      <span className="text-white text-xs">{selectedBonus.stacking_mode}</span>
                    </div>
                  </div>
                </div>

                {/* Promo Code */}
                {selectedBonus.promo_code && (
                  <div>
                    <div className="text-xs text-purple-400 mb-1 font-semibold">{t("bonus2.players.aiPersonalization")}</div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-gray-400">{t("bonus2.players.aiEligible")} </span>
                        <span className="text-white">{t("bonus2.players.yes")}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">{t("bonus2.players.aiPriorityWeight")} </span>
                        <span className="text-white">{selectedBonus.ai_priority_weight}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <p className="text-xs text-gray-400">{t("bonus2.players.assignOnlyNote")}</p>

            {bulkMode ? (
              <button
                onClick={() => setConfirmBulk(true)}
                disabled={loading || tagSlugs.length === 0 || !selectedBonusId || !audience || audience.overCap || audience.total === 0}
                className="w-full px-6 py-3 bg-purple text-white rounded hover:bg-purple-700 transition-colors disabled:bg-gray-600 disabled:cursor-not-allowed"
              >
                {loading ? t("bonus2.players.assigning") : t("bonus2.players.assignToTagged", { count: audience?.total ?? 0 })}
              </button>
            ) : (
              <button
                onClick={handleAssignBonus}
                disabled={loading || !userId || !selectedBonusId}
                className="w-full px-6 py-3 bg-purple text-white rounded hover:bg-purple-700 transition-colors disabled:bg-gray-600 disabled:cursor-not-allowed"
              >
                {loading ? t("bonus2.players.assigning") : t("bonus2.players.assignBonus")}
              </button>
            )}
          </div>
        )}

        {bulkResult && <BulkAssignResults result={bulkResult} onReset={handleReset} />}

        {confirmBulk && (
          <ModalConfirmAction
            data={confirmBulk}
            handler={() => setConfirmBulk(false)}
            text={
              t("bonus2.players.confirmBulkAssign", {
                count: audience?.total ?? 0,
                bonus: getDisplayName(bonuses.find((b) => b.id === selectedBonusId)?.name)
              }) as string
            }
            actionText={t("bonus2.players.assignBonus") as string}
            actionHandler={handleBulkAssign}
          />
        )}

        {/* Step 2: Activate Bonus */}
        {assignedBonus && (
          <div className="space-y-4">
            <div className="bg-emerald-900 border border-emerald-700 rounded p-4 mb-4">
              <div className="flex items-center gap-2 text-emerald-300">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="font-semibold">{t("bonus2.players.bonusAssignedSuccessfully")}</span>
              </div>
              <p className="text-sm text-emerald-400 mt-2">Status: {assignedBonus.status}</p>
            </div>

            {assignedBonus.status === "SUBSCRIBED" && (
              <>
                <div className="bg-[#0C0C1A] border border-gray-600 rounded p-4 space-y-2">
                  <h3 className="text-white font-semibold mb-2">{t("bonus2.players.assignedBonus")}</h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-400">{t("bonus2.players.bonusId")} </span>
                      <span className="text-white font-mono text-xs">{assignedBonus.id}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">{t("bonus2.players.userId")} </span>
                      <span className="text-white">{assignedBonus.user_id}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">{t("bonus2.players.coin2")} </span>
                      <span className="text-white">{assignedBonus.coin}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">{t("bonus2.players.subscribedAt")} </span>
                      <span className="text-white text-xs">{formatDateTime(assignedBonus.subscribed_at)}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t("bonus2.players.depositAmountOptional2")}
                    <span className="text-gray-500 ml-2 text-xs">{t("bonus2.players.leaveEmptyIfNo")}</span>
                  </label>
                  <input
                    type="number"
                    value={depositAmount}
                    onChange={(e) => setDepositAmount(e.target.value)}
                    placeholder="0"
                    className="w-full bg-[#0C0C1A] text-white border border-gray-600 rounded px-4 py-2 focus:outline-none focus:border-purple-500"
                    disabled={loading}
                  />
                  <p className="text-xs text-gray-400 mt-1">{t("bonus2.players.forDepositBonusesEnter")}</p>
                </div>

                <button
                  onClick={handleActivateBonus}
                  disabled={loading}
                  className="w-full px-6 py-3 bg-emerald-600 text-white rounded hover:bg-emerald-700 transition-colors disabled:bg-gray-600 disabled:cursor-not-allowed"
                >
                  {loading ? t("bonus2.players.activating") : t("bonus2.players.activateBonus")}
                </button>
              </>
            )}

            {assignedBonus.status === "ACTIVE" && (
              <div className="bg-[#0C0C1A] border border-emerald-600 rounded p-4 space-y-2">
                <h3 className="text-emerald-400 font-semibold mb-2">✅ Bonus Active!</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-400">{t("bonus2.players.bonusAmount")} </span>
                    <span className="text-white font-semibold">{formatFiatCurrency(Number(assignedBonus.bonus_amount))}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">{t("bonus2.players.wageringRequired")} </span>
                    <span className="text-white font-semibold">{formatFiatCurrency(Number(assignedBonus.wagering_required))}</span>
                  </div>
                  {assignedBonus.deposit_amount && (
                    <div>
                      <span className="text-gray-400">{t("bonus2.players.depositAmount")} </span>
                      <span className="text-white">{formatFiatCurrency(Number(assignedBonus.deposit_amount))}</span>
                    </div>
                  )}
                  {assignedBonus.activated_at && (
                    <div>
                      <span className="text-gray-400">{t("bonus2.players.activatedAt")} </span>
                      <span className="text-white text-xs">{formatDateTime(assignedBonus.activated_at)}</span>
                    </div>
                  )}
                  {assignedBonus.expires_at && (
                    <div>
                      <span className="text-gray-400">{t("bonus2.players.expiresAt")} </span>
                      <span className="text-white text-xs">{formatDateTime(assignedBonus.expires_at)}</span>
                    </div>
                  )}
                  {assignedBonus.max_cashout && (
                    <div>
                      <span className="text-gray-400">{t("bonus2.players.maxCashout")} </span>
                      <span className="text-white">{formatFiatCurrency(Number(assignedBonus.max_cashout))}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <button onClick={handleReset} className="w-full px-6 py-3 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors">
              {t("bonus2.players.assignAnotherBonus")}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
